import React from 'react'

export type HybridEvidenceCellProps = {
  /** Short-read VAF (FORMAT/SAD), or null when absent. */
  sr: number | null
  /** Long-read VAF (FORMAT/LAD), or null when absent. */
  lr: number | null
  /** Max |SR - LR| VAF difference still treated as concordant. */
  threshold?: number
}

const fmt = (v: number | null): string => (v === null ? '-' : v.toFixed(2))
const clamp01 = (v: number): number => Math.min(Math.max(v, 0), 1)

/**
 * Two stacked SR/LR VAF mini-bars (GEN-3140). When both are present the bars are
 * green if the two VAFs agree within `threshold`, amber if they disagree; a lone
 * caller is shown in neutral gray. Renders "-" when neither read type called it.
 */
const HybridEvidenceCell: React.FC<HybridEvidenceCellProps> = ({ sr, lr, threshold = 0.25 }) => {
  if (sr === null && lr === null) return <span className="text-text-subtle">-</span>

  const both = sr !== null && lr !== null
  const agree = both && Math.abs(sr - lr) <= threshold
  const fill = both ? (agree ? 'bg-green-500' : 'bg-amber-500') : 'bg-gray-400'

  const bar = (v: number | null) =>
    v === null ? (
      <div className="h-1 w-8 rounded bg-gray-100" />
    ) : (
      <div className="h-1 w-8 overflow-hidden rounded bg-gray-100">
        <div className={`h-full ${fill}`} style={{ width: `${clamp01(v) * 100}%` }} />
      </div>
    )

  return (
    <span
      className="flex flex-col gap-0.5"
      title={`SR VAF ${fmt(sr)} / LR VAF ${fmt(lr)}${both ? (agree ? ' (agree)' : ' (disagree)') : ''}`}
      data-testid="hybrid-evidence"
      data-agree={both ? (agree ? 'true' : 'false') : 'na'}
    >
      {bar(sr)}
      {bar(lr)}
    </span>
  )
}

export default HybridEvidenceCell
